import React, { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { INVOICE_STATUSES } from "constants/statuses";
import { ROUTES } from "routes";

import { datetime, toDollar } from "utils";

import { useReferrerState } from "hooks";
import { useInvoice, useInvoiceDownload } from "hooks/api";
import { useSnackbar } from "contexts";
import { ArrowUpRightIcon, DownloadIcon, ReceiptTextIcon } from "lucide-react";

import { Box, Divider, Grid, Stack, Typography } from "@mui/material";

import { NARROW_COLUMN_WIDTH } from "config";
import { Button } from "@repo/ui";
import {
  EmptyState,
  Header,
  InfoBlock,
  InfoItem,
  InfoRow,
  CategoryCell,
  UserCell,
  PaymentCell,
} from "components";
import { AddressPreview } from "components/display/AddressPreview";
import { InvoicePageSkeleton } from "./InvoicePageSkeleton";
import type { InvoiceLineItem } from "services/billingApi";

/**
 * Readable-measure cap for the invoice, matching the settings sections' `narrow` flag and
 * `NARROW_COLUMN_WIDTH`.
 *
 * ## Why an invoice is narrow content
 *
 * The `narrow` opt-in on SettingsPage draws the line at "forms and prose" (capped) vs "data
 * tables and the permissions matrix" (left wide, because they use the width). An invoice is
 * neither a form nor a table of many records — it is a DOCUMENT: issuer, bill-to, line items,
 * totals, one of each. It fails the data-table test the same way a form does.
 *
 * The concrete symptom, measured on `#6HR2ZE8S-0003` at a 1920 viewport (content area 1840,
 * sidebar minimised), capped vs uncapped under otherwise identical conditions:
 *
 *   content column   1792 → 1100        (left 1205 → 744, summary rail 603 → 372)
 *   line item        description ends 197px from its own amount → 12px
 *
 * Uncapped, the description fits on one line and the amount is flung to the far edge of a
 * 603px rail; capped, the description wraps to fill 372px and the amount sits one flex gap
 * from it.
 *
 * The gap scales with the window, which is why this is only visible where the rule says it is:
 * the same measurement is 138px at 1920 with the sidebar expanded (content area 1664) and 12px
 * at 1280 either way — at 1280 the description already wraps, so the cap is nearly inert there
 * and the page is unchanged. A printed invoice is ~816px at 96dpi, so 1100 is generous for the
 * document itself.
 *
 * ## The cap wraps the WHOLE page, title included
 *
 * Not just the Grid. WhatsNewPage records what happens otherwise: capping only the content
 * "left the title at the page's left edge while the cards sat centred hundreds of pixels away,
 * reading as two unrelated things." A centred column owns its heading.
 *
 * The loading skeleton is wrapped too — §19 footprint parity is about the space the placeholder
 * reserves, and a 1632px skeleton resolving into an 1100px page is a layout shift.
 *
 * The internal 8/4 split is untouched: at 1100 that is a ~730px document column beside a ~360px
 * summary rail, a sane invoice proportion. The split was doing real work; the unbounded OUTER
 * width was not.
 */
const PageColumn = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: "auto" }}>{children}</Box>
);

// Human label for a single line item — prefers the Stripe description (already
// "N × Plan (at $X / month)"), else builds "N × Product".
const lineItemLabel = (item: InvoiceLineItem): string =>
  item.description ||
  [
    item.quantity ? `${item.quantity} ×` : null,
    item.productName || item.planNickname || item.priceId || "Item",
  ]
    .filter(Boolean)
    .join(" ");

export const InvoicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open: notify } = useSnackbar();
  const { data: invoice, isLoading } = useInvoice(id);
  const downloadInvoice = useInvoiceDownload();
  // Detail → detail: the derived label would be the Invoices list, not this invoice.
  const referrer = useReferrerState("Invoice");

  const handleDownload = useCallback(async () => {
    if (!invoice) return;
    try {
      const { url } = await downloadInvoice.mutateAsync(invoice.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      notify({
        type: "error",
        text: "Unable to download this invoice. Please try again.",
      });
    }
  }, [downloadInvoice, invoice, notify]);

  // Footprint-parity placeholder (§19) in place of the centred `<Loader />`, which reserved none of
  // the ~489px two-column layout it stood in for. See InvoicePageSkeleton for the measurements.
  if (isLoading) {
    return (
      <PageColumn>
        <InvoicePageSkeleton />
      </PageColumn>
    );
  }

  if (!invoice) {
    return (
      <EmptyState
        title="Invoice not found"
        text="We couldn't find the invoice you were looking for."
        icon={<ReceiptTextIcon strokeWidth={1} size={32} />}
        buttons={
          <Button
            variant="primary"
            disableElevation
            onClick={() => navigate(ROUTES.SETTINGS_COMPANY_BILLING)}
          >
            Back to Billing
          </Button>
        }
      />
    );
  }

  const displayId = invoice.number ?? invoice.id;

  const formattedDateTime = invoice.createdAt
    ? datetime(invoice.createdAt, "dd MMM, yyyy, h:mm a")
    : "—";

  // Type is resolved server-side (billing.controller resolveInvoiceType) so the
  // detail page and the invoices table always agree — no client re-resolution.
  const invoiceTypeChip = (
    <CategoryCell
      category={invoice.type ?? "other"}
      filter={false}
      align="start"
      bordered={false}
    />
  );

  const hasCustomer = Boolean(invoice.customerName || invoice.customerEmail);
  const orderedByValue = hasCustomer ? (
    <UserCell
      user={{
        name: invoice.customerName || undefined,
        email: invoice.customerEmail || undefined,
      }}
    />
  ) : (
    "—"
  );

  const hasPaymentMethod = Boolean(
    invoice.paymentMethod &&
      (invoice.paymentMethod.brand ||
        invoice.paymentMethod.last4 ||
        invoice.paymentMethod.label)
  );
  const paymentMethodValue = hasPaymentMethod ? (
    <PaymentCell paymentMethod={invoice.paymentMethod} link={false} />
  ) : (
    "—"
  );

  const shippingValue = invoice.shipping ? (
    <AddressPreview address={invoice.shipping} />
  ) : (
    "—"
  );

  const discount = invoice.lineItems.reduce(
    (sum, item) => sum + (item.amountDiscount || 0),
    0
  );

  const renderTotalRow = (
    label: string,
    value: string,
    strong = false
  ) => (
    <Stack direction="row" spacing={6} justifyContent="flex-end">
      <Typography
        variant={strong ? "body2" : "body3"}
        color={strong ? "text.primary" : "text.secondary"}
        sx={{ fontWeight: strong ? 600 : 400 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ minWidth: 96, textAlign: "right", fontWeight: strong ? 600 : 400 }}
      >
        {value}
      </Typography>
    </Stack>
  );

  return (
    <PageColumn>
      <Header
        title={`#${displayId}`}
        breadcrumbs
        status={{
          statuses: INVOICE_STATUSES,
          value: invoice.status ?? undefined,
        }}
        breadcrumbsOveride={displayId}
        rightComponents={
          <Stack direction="row" spacing={3} alignItems="center">
            {invoice.orderId && (
              <Button
                variant="secondary"
                disableElevation
                startIcon={<ArrowUpRightIcon strokeWidth={1} />}
                onClick={() => navigate(ROUTES.TAGS_ORDER(invoice.orderId!), { state: referrer })}
              >
                View order
              </Button>
            )}
            <Button
              variant="secondary"
              disableElevation
              startIcon={<DownloadIcon strokeWidth={1} />}
              disabled={downloadInvoice.isPending}
              onClick={handleDownload}
            >
              Download PDF
            </Button>
          </Stack>
        }
      />
      {/* Two-column layout — parity with the Order page: details on the left,
          the summary (line items + totals) on the right. */}
      <Grid container spacing={4} sx={{ pt: 4 }}>
        <Grid item xs={12} md={8}>
          <InfoBlock>
            <Stack p={6} pb={4}>
              <Typography variant="body1">Details</Typography>
            </Stack>
            <InfoRow>
              <InfoItem label="Invoice ID" value={`#${displayId}`} />
              <InfoItem label="Date" value={formattedDateTime} />
            </InfoRow>
            <InfoRow>
              <InfoItem
                label="Total"
                value={
                  typeof invoice.total === "number"
                    ? toDollar(invoice.total)
                    : "—"
                }
              />
              <InfoItem label="Type" value={invoiceTypeChip} />
            </InfoRow>
            <InfoRow>
              <InfoItem
                label="Ordered by"
                value={orderedByValue}
                valueSpacing={3}
              />
              <InfoItem
                label="Payment method"
                value={paymentMethodValue}
                valueSpacing={3}
              />
            </InfoRow>
            {invoice.shipping && (
              <>
                <InfoRow>
                  <InfoItem
                    label="Shipping address"
                    value={shippingValue}
                    valueSpacing={3}
                  />
                  {/* A layout SPACER filling the row's second half, not an empty
                      value. It passes an element rather than `null` because
                      `InfoItem` now reads `null` as "we looked and there is
                      nothing" and draws a dash — correct for a real field, wrong
                      for a filler that labels nothing. */}
                  <InfoItem label="" value={<Box />} />
                </InfoRow>
              </>
            )}
          </InfoBlock>
        </Grid>

        <Grid item xs={12} md={4}>
          <InfoBlock>
            {/* Line items as product rows (parity with the Order page's summary):
                name + price, qty subline, then subtotal/discount/total. */}
            <Stack p={6} spacing={4}>
              {/* pb on the title (matching the Order/Cart summary) gives the title→
                  line-items a roomier gap for parity across the summary panels. */}
              <Typography variant="body1" pb={6}>
                Invoice summary
              </Typography>
              {invoice.lineItems.length > 0 && (
                <Stack spacing={5}>
                  {invoice.lineItems.map((item) => (
                    <Stack spacing={3} key={item.id}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={3}
                      >
                        <Typography variant="body2">
                          {item.productName ||
                            item.planNickname ||
                            lineItemLabel(item)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {typeof item.amount === "number"
                            ? toDollar(item.amount)
                            : "—"}
                        </Typography>
                      </Stack>
                      {typeof item.quantity === "number" &&
                        item.quantity > 1 && (
                          <Typography variant="body3" color="text.secondary">
                            Qty {item.quantity}
                          </Typography>
                        )}
                      <Divider />
                    </Stack>
                  ))}
                </Stack>
              )}
              <Stack spacing={2}>
                {typeof invoice.subtotal === "number" &&
                  renderTotalRow("Subtotal", toDollar(invoice.subtotal))}
                {discount > 0 &&
                  renderTotalRow("Discount", `- ${toDollar(discount)}`)}
                {typeof invoice.total === "number" &&
                  renderTotalRow("Total", toDollar(invoice.total), true)}
              </Stack>
            </Stack>
          </InfoBlock>
        </Grid>
      </Grid>
    </PageColumn>
  );
};
